import type { QuoteSideKey } from './security-types'

// Security field names as sent on the wire. Some (caller-tier pre-fee quotes)
// are not in the generated SORM schema yet; requests name them as plain
// strings and the fetchers drop any the runtime schema does not serve.
// Tax-aware yields and filter-list membership are computed columns.

export const SECURITY_ENTITY = 'Security'

export const SECURITY_ID_FIELD = 'security_id'

export interface SideBookFields {
  price: string
  ytw: string
  ytm: string
  quantity: string
  minQuantity: string
  minIncrement: string
  preFeePrice: string
  preFeeYtw: string
  preFeeYtm: string
  effectiveMarkup: string
}

export interface SideEvalFields {
  price: string
  ytw: string
  ytm: string
}

const BOOK_FIELDS: Readonly<Record<QuoteSideKey, SideBookFields>> = {
  bid: {
    price: 'best_bid_price',
    ytw: 'best_bid_ytw',
    ytm: 'best_bid_ytm',
    quantity: 'best_bid_qty',
    minQuantity: 'best_bid_min_quantity',
    minIncrement: 'best_bid_min_increment',
    preFeePrice: 'best_bid_pre_fee_price',
    preFeeYtw: 'best_bid_pre_fee_ytw',
    preFeeYtm: 'best_bid_pre_fee_ytm',
    effectiveMarkup: 'best_bid_effective_markup',
  },
  ask: {
    price: 'best_ask_price',
    ytw: 'best_ask_ytw',
    ytm: 'best_ask_ytm',
    quantity: 'best_ask_qty',
    minQuantity: 'best_ask_min_quantity',
    minIncrement: 'best_ask_min_increment',
    preFeePrice: 'best_ask_pre_fee_price',
    preFeeYtw: 'best_ask_pre_fee_ytw',
    preFeeYtm: 'best_ask_pre_fee_ytm',
    effectiveMarkup: 'best_ask_effective_markup',
  },
}

const EVAL_FIELDS: Readonly<Record<QuoteSideKey, SideEvalFields>> = {
  bid: {
    price: 'mpe_bid_price',
    ytw: 'mpe_bid_ytw',
    ytm: 'mpe_bid_ytm',
  },
  ask: {
    price: 'mpe_ask_price',
    ytw: 'mpe_ask_ytw',
    ytm: 'mpe_ask_ytm',
  },
}

/** Post-fee top of book (plus the caller-tier pre-fee view) on one side. */
export function bookFields(side: QuoteSideKey): SideBookFields {
  return BOOK_FIELDS[side]
}

/** Moment price estimate on one side. */
export function evalFields(side: QuoteSideKey): SideEvalFields {
  return EVAL_FIELDS[side]
}

export const MID_EVAL_FIELDS = {
  price: 'mpe_price',
  ytw: 'mpe_ytw',
  ytm: 'mpe_ytm',
} as const

/** Whether Security has a live quote on one side. */
export function quotedFlagField(side: QuoteSideKey): string {
  return side === 'ask' ? 'has_ask' : 'has_bid'
}

/** Every field the row adapter reads to build one side's book. */
export function sideQuoteFieldNames(side: QuoteSideKey): string[] {
  const book = bookFields(side)
  const evals = evalFields(side)
  return [
    book.price,
    book.ytw,
    book.ytm,
    book.quantity,
    book.minQuantity,
    book.minIncrement,
    book.preFeePrice,
    book.preFeeYtw,
    book.preFeeYtm,
    book.effectiveMarkup,
    evals.price,
    evals.ytw,
    evals.ytm,
  ]
}

export const RISK_GROUP_REQUEST_PARAM = 'risk_group_id'
